import React from 'react';
import { ArrowLeft, MapPin, Plane, Calendar, Users, DollarSign, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import SwoopyLogo from '../components/SwoopyLogo';

export default function DestinationPlanPage({ userProfile, onGeneratePlan, onBack }) {
  const [formData, setFormData] = React.useState({
    destination: '',
    departureCity: '',
    duration: '',
    travelers: '1',
    budget: '',
    includeFlights: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.destination.trim()) {
      toast.error('Where are we heading? 🗺️');
      return;
    }
    if (!formData.departureCity.trim()) {
      toast.error('Where are you flying from?');
      return;
    }
    if (!formData.duration || formData.duration < 1) {
      toast.error('How many days is the trip?');
      return;
    }
    if (!formData.travelers || formData.travelers < 1) {
      toast.error('How many people are going?');
      return;
    }

    await onGeneratePlan({
      destination: formData.destination.trim(),
      departureCity: formData.departureCity.trim(),
      duration: parseInt(formData.duration),
      travelers: parseInt(formData.travelers),
      budget: formData.budget ? parseFloat(formData.budget) : null,
      includeFlights: formData.includeFlights
    });
  };

  const getCurrencySymbol = () => {
    const symbols = { 'IDR': 'Rp', 'USD': '$', 'EUR': '€', 'GBP': '£', 'SGD': 'S$' };
    return symbols[userProfile?.currency] || userProfile?.currency || 'Rp';
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg)' }}>
      {/* Header */}
      <header 
        className="sticky top-0 z-50 px-4 py-3"
        style={{ backgroundColor: 'var(--bg)' }}
      >
        <div className="max-w-md mx-auto flex items-center">
          <button 
            onClick={onBack} 
            className="flex items-center gap-2 p-2 -ml-2 rounded-full transition-colors"
            style={{ color: 'var(--muted)' }}
          >
            <ArrowLeft size={20} />
            <span className="text-sm font-medium">Back</span>
          </button>
        </div>
      </header>

      <div className="swoopy-container pb-8">
        {/* Welcome */}
        <div className="text-center py-6">
          <h1 className="text-2xl font-semibold mb-2" style={{ color: 'var(--text)' }}>
            Hey {userProfile?.name || 'there'}! 👋
          </h1>
          <p style={{ color: 'var(--muted)' }}>
            Let's plan something amazing
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Destination */}
          <div className="space-y-2">
            <label className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text)' }}>
              <MapPin size={16} style={{ color: 'var(--coral)' }} />
              Where do you want to go?
            </label>
            <div className="swoopy-search">
              <MapPin className="icon" size={18} />
              <input
                type="text"
                placeholder="e.g., Bali, Paris, Tokyo"
                value={formData.destination}
                onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                autoFocus
              />
            </div>
          </div>

          {/* Departure */}
          <div className="space-y-2">
            <label className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text)' }}>
              <Plane size={16} style={{ color: 'var(--coral)' }} />
              Flying from?
            </label>
            <div className="swoopy-search">
              <Plane className="icon" size={18} />
              <input
                type="text"
                placeholder="e.g., Jakarta, Singapore"
                value={formData.departureCity}
                onChange={(e) => setFormData({ ...formData, departureCity: e.target.value })}
              />
            </div>
          </div>

          {/* Duration & Travelers */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text)' }}>
                <Calendar size={16} style={{ color: 'var(--coral)' }} />
                How long?
              </label>
              <div className="swoopy-search">
                <input
                  type="number"
                  min="1"
                  max="90"
                  placeholder="7"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  style={{ textAlign: 'center' }}
                />
                <span style={{ color: 'var(--muted)', fontSize: '14px', marginLeft: '4px' }}>days</span>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text)' }}>
                <Users size={16} style={{ color: 'var(--coral)' }} />
                How many?
              </label>
              <div className="swoopy-search">
                <input
                  type="number"
                  min="1"
                  max="20"
                  placeholder="2"
                  value={formData.travelers}
                  onChange={(e) => setFormData({ ...formData, travelers: e.target.value })}
                  style={{ textAlign: 'center' }}
                />
                <span style={{ color: 'var(--muted)', fontSize: '14px', marginLeft: '4px' }}>people</span>
              </div>
            </div>
          </div>

          {/* Budget */}
          <div className="space-y-2">
            <label className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text)' }}>
              <DollarSign size={16} style={{ color: 'var(--coral)' }} />
              Budget (optional)
            </label>
            <div className="swoopy-search">
              <span style={{ color: 'var(--muted)', fontSize: '15px', marginRight: '4px' }}>{getCurrencySymbol()}</span>
              <input
                type="number"
                min="0"
                placeholder="5,000,000"
                value={formData.budget}
                onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
              />
            </div>
            <label className="flex items-center gap-3 mt-3 p-3 rounded-xl cursor-pointer" style={{ backgroundColor: 'var(--coral-soft)' }}>
              <input
                type="checkbox"
                checked={formData.includeFlights}
                onChange={(e) => setFormData({ ...formData, includeFlights: e.target.checked })}
                className="w-4 h-4 rounded"
                style={{ accentColor: 'var(--coral)' }}
              />
              <span className="text-sm" style={{ color: 'var(--text)' }}>Budget includes flights</span>
            </label>
          </div>

          {/* Submit */}
          <button
            type="submit"
            className="swoopy-btn w-full gap-2 mt-6"
            data-testid="generate-plan-btn"
          >
            <Sparkles size={18} />
            Get my Swoopy Picks
          </button>
        </form>

        {/* Info */}
        <div 
          className="mt-6 p-4 rounded-2xl text-center text-sm"
          style={{ backgroundColor: 'var(--coral-soft)', color: 'var(--text)' }}
        >
          We'll find trips that match your <strong>{userProfile?.travelStyle || 'mid-range'}</strong> vibe 💫
        </div>
      </div>
    </div>
  );
}
